// Enhanced Loading Component with Beautiful Animations
import React from 'react';
import { Loader2, Sparkles, BookOpen, Zap, Brain } from 'lucide-react';

interface LoadingSpinnerProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  context?: 'article' | 'roadmap' | 'video' | 'auth' | 'data' | 'general';
  fullScreen?: boolean;
  progress?: number; // 0-100
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  message = 'Loading...', 
  size = 'md',
  context = 'general',
  fullScreen = false,
  progress
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  const contextIcons = {
    article: Brain,
    roadmap: BookOpen,
    video: Sparkles,
    auth: Zap,
    data: Loader2,
    general: Loader2
  };

  const contextMessages = {
    article: 'Generating article content...',
    roadmap: 'Building your learning path...',
    video: 'Finding perfect videos...',
    auth: 'Authenticating...',
    data: 'Loading data...',
    general: 'Loading...'
  };

  const Icon = contextIcons[context];
  const defaultMessage = contextMessages[context];

  const containerClasses = fullScreen
    ? "fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-md"
    : "flex flex-col items-center justify-center gap-4 py-8";

  return (
    <div className={containerClasses}>
      <div className="relative flex flex-col items-center gap-4">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 -z-10 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-rose-500 to-orange-500 animate-gradient-x blur-3xl" />
        </div>

        {/* Main Spinner */}
        <div className="relative">
          {/* Outer Ring */}
          <div className={`${sizeClasses[size]} rounded-full border-4 border-red-200 dark:border-red-900 animate-pulse`} />
          
          {/* Spinning Ring */}
          <div className={`absolute inset-0 ${sizeClasses[size]} rounded-full border-4 border-transparent border-t-red-600 border-r-rose-600 animate-spin`} />
          
          {/* Inner Icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className={`${size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-5 h-5' : size === 'lg' ? 'w-8 h-8' : 'w-12 h-12'} text-red-600 dark:text-red-400 animate-pulse`} />
          </div>
        </div>

        {/* Progress Bar */}
        {progress !== undefined && (
          <div className="w-64 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-red-600 via-rose-600 to-orange-600 transition-all duration-500 ease-out rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Loading Message */}
        <div className="text-center space-y-2">
          <p className="text-base md:text-lg font-medium text-slate-900 dark:text-slate-100 animate-fade-in">
            {message || defaultMessage}
          </p>
          {progress !== undefined && (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {progress}% complete
            </p>
          )}
        </div>

        {/* Floating Dots Animation */}
        <div className="flex gap-2">
          <div className="w-2 h-2 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-pink-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
};

// Add gradient animation to tailwind config
// @keyframes gradient-x {
//   0%, 100% { background-position: 0% 50%; }
//   50% { background-position: 100% 50%; }
// }
